import { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";

import { useFlags } from "@stadium/services/launch-darkly/use-flags";
import { useQuery } from "@stadium/services/apollo-client";

import { fetchVariantsList } from "@graphql/queries/stores/products/fetch-variant-list";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";

import { getStoreCurrencyCode, getStoreNumber, getIsLockerShop } from "@store/selectors/store";
import { getCountry } from "@store/selectors/country";

import { isGlobalCountry } from "@stadium/helpers/country/utils";
import { computeExternalSourceIds, getUpdatedLineItemFromVariants } from "@helpers/orders/cart";

import { variantListLimitQueryFieldsFlag } from "@stadium/services/launch-darkly/constants";

import { ASSET_KINDS } from "@stadium/shared/constants/logo";

const emptyObj = {};

const DEFAULT_DETAILS = {
  lineItemsWithDetails: [],
  productSetItemsWithDetails: [],
  medleySetsWithDetails: [],
};

const useGetVariantDetails = ({
  lineItems,
  productSets,
  medleySets,
  skipFetchVariants = false,
}) => {
  const [productsWithVariantDetails, setProductsWithVariantDetails] = useState(DEFAULT_DETAILS);
  const { [variantListLimitQueryFieldsFlag]: variantListLimitQueryFieldsFlagEnabled } = useFlags();

  const storeNumber = useSelector(getStoreNumber);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const country = useSelector(getCountry);

  const isLockerShop = useSelector(getIsLockerShop);

  const logoIds = lineItems.map((item) => item.lineItemCustomization?.logoId).filter(Boolean);

  const { volumeDiscountProductsVariables, isSendToOthers } = useSendItemsContext();

  // added this to add variant details on line items again when user updates line items from cart screen.
  const { quantity: cartItemsQuantity, dietaryPreference: cartItemsPreferences } = useMemo(() => {
    return [...lineItems, ...productSets, ...medleySets].reduce(
      (acc, product) => {
        const { quantity, dietaryPreference, dietaryRestriction, restrictions } = product;
        const preferences = [
          dietaryPreference,
          dietaryRestriction?.join(""),
          restrictions?.join(""),
        ]
          .filter((str) => str)
          .join("");
        acc.quantity = acc.quantity + quantity;
        acc.dietaryPreference = acc.dietaryPreference + preferences;
        return acc;
      },
      { quantity: 0, dietaryPreference: "" },
    );
  }, [lineItems, productSets, medleySets]);

  const { iso: countryIso = "" } = country;

  const { isGuestOrPreviewMode } = usePreviewModeContext();

  const { externalSourceIds, catalogExternalStoreProductMappings } = useMemo(
    () => computeExternalSourceIds({ lineItems, productSets, medleySets }),
    [lineItems, productSets, medleySets],
  );

  const globalZone = isGlobalCountry(country);

  const fetchDigitalAssets = Boolean(isLockerShop && logoIds?.length > 0);

  const variables = {
    globalZone,
    country: globalZone ? "" : countryIso,
    currency: storeCurrencyCode,
    storeNumber,
    catalogExternalStoreProductMappings,
    externalSourceIds,
    // required to fetch variant specific images for theme box products.
    isProductVariantsListRequired: !isEmpty(productSets),
    isVaraintSelectionDataRequired: true,
    ...volumeDiscountProductsVariables,
    fetchDigitalAssets,
    ...(fetchDigitalAssets && {
      ids: logoIds,
      kind: ASSET_KINDS.logo,
    }),
    limitQueryFields: Boolean(variantListLimitQueryFieldsFlagEnabled) && !isSendToOthers,
  };

  const { data: variantData, loading: variantDataloading } = useQuery(fetchVariantsList, {
    variables,
    skip: variables.externalSourceIds.length === 0 || isGuestOrPreviewMode || skipFetchVariants,
    fetchPolicy: "cache-and-network",
  });

  const variantsMapping = useMemo(() => {
    if (variantDataloading || !variantData) {
      return emptyObj;
    }
    return groupBy(variantData?.variants, "externalId");
  }, [variantDataloading, variantData]);

  const getUpdatedItem = (item) => {
    if (isGuestOrPreviewMode) return { ...item, masterVariant: item.product?.masterVariant };
    return getUpdatedLineItemFromVariants(item, variantsMapping, isSendToOthers);
  };

  //lineItems does needs to be added in the dependency array to avoid infinite loop
  useEffect(() => {
    if (!isEmpty(variantsMapping)) {
      const lineItemsWithDetails = lineItems.map(getUpdatedItem);
      const productSetItemsWithDetails = productSets.map(getUpdatedItem);
      const medleySetsWithDetails = medleySets.map(getUpdatedItem);
      medleySetsWithDetails.forEach(
        (item) => (item.medleyComponents = item.medleyComponents.map(getUpdatedItem)),
      );
      const productsWithVariantDetails = {
        lineItemsWithDetails,
        productSetItemsWithDetails,
        medleySetsWithDetails,
      };

      setProductsWithVariantDetails(productsWithVariantDetails);
    }
  }, [variantsMapping, cartItemsQuantity, cartItemsPreferences]);

  return {
    variantData,
    variantDataloading,
    variantsMapping,
    productsWithVariantDetails,
  };
};
export default useGetVariantDetails;
